import React from "react";
import { Row, Col, Card, ProgressBar, Table, Badge, Alert } from "react-bootstrap";
import { FaServer,  FaMemory, FaChartBar, FaGlobe, FaClock, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { useGetAIStatsQuery, useGetAIModelsQuery } from "../../../redux/api/AIPlatformApi/aiPlatformApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { FaComputer } from "react-icons/fa6";

const AIDashboard = () => {
  const { data: statsData, isLoading: isLoadingStats, error: statsError } = useGetAIStatsQuery(undefined, {
    pollingInterval: 10000, // Refresh every 10 seconds
  });
  const { data: modelsData, isLoading: isLoadingModels, error: modelsError } = useGetAIModelsQuery(undefined, {
    pollingInterval: 5000, // Refresh models every 5 seconds
  });

  const isOffline = statsError || modelsError;

  // Fallback demo data if backend is offline or loading fails
  const demoStats = {
    totalRequests: 142850,
    avgResponseTimeMs: 184,
    cacheHitRate: 38.5,
    requestsByFeature: {
      translate: 54200,
      replies: 31200,
      sentiment: 21900,
      summarize: 14800,
      censor: 12100,
      moderate: 8650,
    },
    requestsByApplication: {
      "iChat App": 78200,
      "Corporate Portal": 35400,
      "Support Bot": 21300,
      "Ideal Web Portal": 7950,
    }
  };

  const demoModels = {
    models: {
      ollama: "running",
      nllb: "running",
    },
    system: {
      cpuUsage: 24,
      memoryUsage: 68,
      usedMemoryMB: 11140,
      totalMemoryMB: 16384,
    }
  };

  const stats = statsData?.data || demoStats;
  const models = modelsData?.data || demoModels;

  if (isLoadingStats || isLoadingModels) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoaderSpinner />
      </div>
    );
  }

  // Calculate percentages for feature breakdown
  const featureTotals = Object.values(stats.requestsByFeature || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="fade-in">
      {isOffline && (
        <Alert variant="warning" className="d-flex align-items-center gap-2 rounded-4 shadow-sm mb-4 border-warning">
          <FaExclamationCircle className="text-warning flex-shrink-0" size={20} />
          <div>
            <strong>Demo Mode:</strong> The central AI Platform service is offline or inaccessible. Displaying simulated real-time telemetry metrics.
          </div>
        </Alert>
      )}

      {/* Stats Cards Row */}
      <Row className="mb-4 g-4">
        <Col xs={12} sm={6} lg={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100 p-3 stat-card">
            <Card.Body className="d-flex align-items-center justify-content-between p-2">
              <div>
                <span className="text-muted small text-uppercase fw-bold">All-Time Queries</span>
                <h3 className="fw-bold mb-0 mt-1">{stats.totalRequests?.toLocaleString()}</h3>
              </div>
              <div className="stat-icon-wrapper bg-primary bg-opacity-10 text-primary">
                <FaChartBar size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100 p-3 stat-card">
            <Card.Body className="d-flex align-items-center justify-content-between p-2">
              <div>
                <span className="text-muted small text-uppercase fw-bold">Avg Latency</span>
                <h3 className="fw-bold mb-0 mt-1">{stats.avgResponseTimeMs} ms</h3>
              </div>
              <div className="stat-icon-wrapper bg-warning bg-opacity-10 text-warning">
                <FaClock size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100 p-3 stat-card">
            <Card.Body className="d-flex align-items-center justify-content-between p-2">
              <div>
                <span className="text-muted small text-uppercase fw-bold">Cache Hit Rate</span>
                <h3 className="fw-bold mb-0 mt-1">{stats.cacheHitRate}%</h3>
              </div>
              <div className="stat-icon-wrapper bg-success bg-opacity-10 text-success">
                <FaGlobe size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        {/* Hardware Status */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-1">
              <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <FaServer className="text-primary" /> Core Server Telemetry
              </h5>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-secondary d-flex align-items-center gap-2">
                    <FaComputer className="text-muted" /> CPU Allocation
                  </span>
                  <span className="fw-bold text-dark">{models.system?.cpuUsage}%</span>
                </div>
                <ProgressBar 
                  now={models.system?.cpuUsage} 
                  variant={models.system?.cpuUsage > 80 ? "danger" : models.system?.cpuUsage > 50 ? "warning" : "primary"}
                  className="rounded-pill"
                  style={{ height: "10px" }}
                />
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-secondary d-flex align-items-center gap-2">
                    <FaMemory className="text-muted" /> RAM Utilization
                  </span>
                  <span className="fw-bold text-dark">{models.system?.memoryUsage}%</span>
                </div>
                <ProgressBar 
                  now={models.system?.memoryUsage} 
                  variant={models.system?.memoryUsage > 80 ? "danger" : "success"}
                  className="rounded-pill"
                  style={{ height: "10px" }}
                />
                <div className="text-muted small mt-1 text-end">
                  {(models.system?.usedMemoryMB / 1024).toFixed(1)} GB of {(models.system?.totalMemoryMB / 1024).toFixed(0)} GB used
                </div>
              </div>

              <hr />

              <h6 className="fw-bold text-dark mb-3 mt-2">Model Deployments Status</h6>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded-3 border">
                  <div>
                    <strong className="text-dark block">Ollama (LLM Gateway)</strong>
                    <div className="text-muted small">Powers Smart Replies, Censor, Censors & Summarization</div>
                  </div>
                  <Badge bg={models.models?.ollama === "running" ? "success" : "danger"} className="px-3 py-2 rounded-pill text-capitalize">
                    {models.models?.ollama === "running" ? <><FaCheckCircle className="me-1" /> Active</> : "Down"}
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded-3 border">
                  <div>
                    <strong className="text-dark block">NLLB (Translation Engine)</strong>
                    <div className="text-muted small">Powers multilingual dictionary mapping & translator APIs</div>
                  </div>
                  <Badge bg={models.models?.nllb === "running" ? "success" : "danger"} className="px-3 py-2 rounded-pill text-capitalize">
                    {models.models?.nllb === "running" ? <><FaCheckCircle className="me-1" /> Active</> : "Down"}
                  </Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Feature Breakdown Table */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-1">
              <h5 className="fw-bold text-dark mb-0">API Usage Breakdown by Feature</h5>
            </Card.Header>
            <Card.Body className="p-0 px-4 pb-4">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 mt-2">
                  <thead>
                    <tr className="text-muted small text-uppercase border-bottom-2">
                      <th>AI Service Feature</th>
                      <th className="text-end">Calls Count</th>
                      <th className="text-end" style={{ width: "30%" }}>Volume Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.requestsByFeature || {}).map(([feature, count]) => {
                      const share = featureTotals > 0 ? ((count / featureTotals) * 100).toFixed(1) : 0;
                      return (
                        <tr key={feature}>
                          <td className="fw-semibold text-capitalize text-dark">{feature}</td>
                          <td className="text-end font-monospace">{count?.toLocaleString()}</td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <span className="small text-muted">{share}%</span>
                              <div style={{ width: "60px" }}>
                                <ProgressBar now={parseFloat(share)} variant="primary" style={{ height: "6px" }} className="mb-0" />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Applications Breakdown */}
        <Col sm={12}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-1">
              <h5 className="fw-bold text-dark mb-0">Client Applications Volume Distribution</h5>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 mt-2">
                  <thead>
                    <tr className="text-muted small text-uppercase">
                      <th>Application Client</th>
                      <th className="text-end">Total Traffic Queries</th>
                      <th className="text-end">Percentage Load</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.requestsByApplication || {}).map(([app, count]) => {
                      const share = featureTotals > 0 ? ((count / featureTotals) * 100).toFixed(1) : 0;
                      return (
                        <tr key={app}>
                          <td className="fw-bold text-dark">{app}</td>
                          <td className="text-end font-monospace">{count?.toLocaleString()}</td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-3">
                              <span className="small fw-semibold">{share}%</span>
                              <ProgressBar now={parseFloat(share)} variant="info" style={{ height: "8px", width: "150px" }} className="mb-0 rounded-pill" />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .stat-card {
          border-left: 4px solid var(--bs-primary) !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05) !important;
        }
        .stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default AIDashboard;
